import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { adminApi, errMsg } from "@/lib/api";

function EditDialog({ target, onClose, onDone }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { setValue(target?.kind === "username" ? target.user.username : ""); setError(""); }, [target]);
  if (!target) return null;
  const isName = target.kind === "username";
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isName) await adminApi.put(`/users/${target.user.id}/username`, { username: value });
      else await adminApi.post(`/users/${target.user.id}/reset-password`, { password: value });
      toast.success(isName ? "Username changed" : "Password reset");
      onDone();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="admin-user-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{isName ? "Change username" : "Reset password"}</DialogTitle><DialogDescription>{target.user.name} · {target.user.business_name}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input autoFocus type={isName ? "text" : "password"} className="h-11" value={value} onChange={(e) => { setValue(e.target.value); setError(""); }} data-testid="admin-user-input" />
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid="admin-user-error">{error}</div>}
          <button className="h-11 w-full rounded-xl bg-slate-900 font-semibold text-white" data-testid="admin-user-submit">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const load = useCallback(() => adminApi.get("/users", { params: { q } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const toggle = async (u) => {
    try {
      await adminApi.post(`/users/${u.id}/${u.status === "Active" ? "deactivate" : "reactivate"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Users</h1>
          <p className="text-sm text-slate-500">{rows ? `${rows.length} users across all businesses` : "Loading…"}</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, username, email, business" data-testid="admin-users-search" className="h-11 w-72 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-slate-900" />
        </div>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-users-table">
          <thead className="border-b border-slate-200 bg-slate-50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{["Name", "Username / email", "Role", "Business", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows?.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.login}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600" data-testid={`admin-user-login-${u.id}`}>{u.login}</td>
                <td className="px-4 py-3">{u.role_label}</td>
                <td className="px-4 py-3 text-slate-600">{u.business_name}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${u.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid={`admin-user-status-${u.id}`}>{u.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {u.username && <button onClick={() => setTarget({ kind: "username", user: u })} data-testid={`admin-user-rename-${u.id}`} className="h-9 whitespace-nowrap rounded-lg border border-slate-200 px-3 text-xs font-semibold hover:border-slate-900">Change username</button>}
                    <button onClick={() => setTarget({ kind: "password", user: u })} data-testid={`admin-user-reset-${u.id}`} className="h-9 whitespace-nowrap rounded-lg border border-slate-200 px-3 text-xs font-semibold hover:border-slate-900">Reset password</button>
                    <button onClick={() => toggle(u)} data-testid={`admin-user-toggle-${u.id}`} className={`h-9 rounded-lg px-3 text-xs font-bold ${u.status === "Active" ? "text-rose-600 hover:bg-rose-50" : "text-emerald-700 hover:bg-emerald-50"}`}>{u.status === "Active" ? "Deactivate" : "Reactivate"}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EditDialog target={target} onClose={() => setTarget(null)} onDone={load} />
    </div>
  );
}
